import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { Banner } from "@/components/Interface/Banner/Banner";
import { usePortalBanners } from "@/hooks/usePortalBanners";
import { getBannerCta, getBannerText } from "@/services/supabase/banners";
import { BannerPlacement, PortalBanner } from "@/services/supabase/types";

const DISMISSED_BANNERS_STORAGE_KEY = "kp-dismissed-banners";
const WARNING_DISMISS_DURATION_MS = 10 * 60 * 1000;
type DismissedBannersState = Record<string, string>;

const getDismissedBanners = () => {
  try {
    const value = window.localStorage.getItem(DISMISSED_BANNERS_STORAGE_KEY);
    if (!value) {
      return {};
    }

    const parsed = JSON.parse(value) as unknown;
    if (Array.isArray(parsed)) {
      // Backward compatibility with old array-based storage.
      return parsed.reduce((acc, bannerId) => {
        acc[String(bannerId)] = new Date(0).toISOString();

        return acc;
      }, {} as DismissedBannersState);
    }

    return parsed as DismissedBannersState;
  } catch {
    return {};
  }
};

const saveDismissedBanners = (dismissedBanners: DismissedBannersState) => {
  window.localStorage.setItem(
    DISMISSED_BANNERS_STORAGE_KEY,
    JSON.stringify(dismissedBanners),
  );
};

const isBannerDismissed = (
  banner: PortalBanner,
  dismissedBanners: DismissedBannersState,
) => {
  const dismissedAt = dismissedBanners[String(banner.id)];
  if (!dismissedAt) {
    return false;
  }

  if (banner.type === "warning") {
    return (
      Date.now() - new Date(dismissedAt).getTime() < WARNING_DISMISS_DURATION_MS
    );
  }

  // News (and all other non-warning types) stay hidden permanently.
  return true;
};

export const PortalTopBanners = ({
  placement,
  className,
}: {
  placement: BannerPlacement;
  className?: string;
}) => {
  const { i18n } = useTranslation();
  const { banners, isLoading } = usePortalBanners(placement);
  const [dismissedBanners, setDismissedBanners] =
    useState<DismissedBannersState>(getDismissedBanners);

  if (isLoading) {
    return null;
  }

  const visibleBanners = banners.filter(
    (banner) =>
      !banner.is_dismissible || !isBannerDismissed(banner, dismissedBanners),
  );

  if (!visibleBanners.length) {
    return null;
  }

  return (
    <div className={clsx("sticky top-0 z-10 flex w-full flex-col", className)}>
      {visibleBanners.map((banner) => {
        const text = getBannerText(banner, i18n.resolvedLanguage);
        const cta = getBannerCta(banner, i18n.resolvedLanguage);
        const isWarning = banner.type === "warning";

        if (!text) {
          return null;
        }

        return (
          <Banner
            key={banner.id}
            variant={isWarning ? "warning" : "news"}
            text={text}
            cta={
              cta?.label && cta.link
                ? { label: cta.label, link: cta.link }
                : undefined
            }
            onDismiss={
              banner.is_dismissible
                ? () => {
                    const nextDismissedBanners = {
                      ...dismissedBanners,
                      [String(banner.id)]: new Date().toISOString(),
                    };
                    setDismissedBanners(nextDismissedBanners);
                    saveDismissedBanners(nextDismissedBanners);
                  }
                : undefined
            }
          />
        );
      })}
    </div>
  );
};
